import { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Plus,
    Search,
    RefreshCw,
    Edit,
    Trash2,
    MoreHorizontal,
    TrendingUp,
    Users,
    AlertTriangle,
    XCircle,
    CreditCard,
    RotateCcw,
    Receipt,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { cn } from '../lib/cn';
import {
    addMonths,
    getSubscriptionStatus,
    getDaysUntilRenewal,
    formatCurrency,
} from '../lib/subscriptionUtils';
import {
    fetchLastAutoInvoiceRun,
    getBillingPeriodLabel,
    type AutoInvoiceRun,
} from '../lib/subscriptionInvoiceUtils';
import { SubscriptionFormModal, type CustomerSubscription } from '../components/SubscriptionFormModal';
import { SubscriptionInvoiceModal } from '../components/SubscriptionInvoiceModal';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';

type FilterType = 'all' | 'active' | 'warning' | 'expired' | 'cancelled';

export default function SubscriptionsPage() {
    const { addNotification } = useNotifications();
    const [subscriptions, setSubscriptions] = useState<CustomerSubscription[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<FilterType>('all');
    const [showForm, setShowForm] = useState(false);
    const [editingSubscription, setEditingSubscription] = useState<CustomerSubscription | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [invoicingSubscription, setInvoicingSubscription] = useState<CustomerSubscription | null>(null);
    const [lastRun, setLastRun] = useState<AutoInvoiceRun | null>(null);

    const loadSubscriptions = useCallback(async () => {
        setLoading(true);
        const [{ data, error }, run] = await Promise.all([
            supabase
                .from('customer_subscriptions')
                .select('*, customers(company_name, codice_cliente)')
                .order('next_renewal_date', { ascending: true }),
            fetchLastAutoInvoiceRun(),
        ]);

        if (error) {
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le subscriptions' });
        } else {
            setSubscriptions(data as CustomerSubscription[]);
        }
        setLastRun(run);
        setLoading(false);
    }, [addNotification]);

    useEffect(() => {
        loadSubscriptions();
    }, [loadSubscriptions]);

    const stats = useMemo(() => {
        const active = subscriptions.filter(s => s.status === 'active');
        const mrr = active.reduce((sum, s) => sum + (s.monthly_amount || 0), 0);
        const warning = active.filter(s => getSubscriptionStatus(s.next_renewal_date) === 'warning').length;
        const expired = active.filter(s => getSubscriptionStatus(s.next_renewal_date) === 'expired').length;
        return { activeCount: active.length, mrr, warning, expired };
    }, [subscriptions]);

    const filtered = useMemo(() => {
        let list = subscriptions;

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(s =>
                (s.customers as any)?.company_name?.toLowerCase().includes(q) ||
                (s.customers as any)?.codice_cliente?.toLowerCase().includes(q)
            );
        }

        switch (filter) {
            case 'active':
                list = list.filter(s => s.status === 'active' && getSubscriptionStatus(s.next_renewal_date) === 'ok');
                break;
            case 'warning':
                list = list.filter(s => s.status === 'active' && getSubscriptionStatus(s.next_renewal_date) === 'warning');
                break;
            case 'expired':
                list = list.filter(s => s.status === 'active' && getSubscriptionStatus(s.next_renewal_date) === 'expired');
                break;
            case 'cancelled':
                list = list.filter(s => s.status === 'cancelled');
                break;
        }

        return list;
    }, [subscriptions, search, filter]);

    // Il tasto Fattura compare quando la prossima fattura è vicina (entro 30
    // giorni) o già passata. Serve ad anticipare il giro notturno, o a
    // fatturare i contratti senza bozza automatica.
    const canInvoice = (sub: CustomerSubscription): boolean => {
        if (sub.status === 'cancelled') return false;
        return getSubscriptionStatus(sub.next_billing_date) !== 'ok';
    };

    const handleEdit = (sub: CustomerSubscription) => {
        setEditingSubscription(sub);
        setShowForm(true);
    };

    const handleNew = () => {
        setEditingSubscription(null);
        setShowForm(true);
    };

    const handleDelete = async () => {
        if (!deletingId) return;
        const { error } = await supabase
            .from('customer_subscriptions')
            .delete()
            .eq('id', deletingId);

        if (error) {
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile eliminare la subscription' });
        } else {
            addNotification({ type: 'success', title: 'Eliminata', message: 'Subscription eliminata' });
            loadSubscriptions();
        }
        setDeletingId(null);
    };

    // Il rinnovo allunga lo stesso contratto: la scadenza avanza di una durata.
    // Prima nasceva un contratto nuovo, che ripartiva da capo con la
    // fatturazione e staccava le fatture già fatte da quelle future.
    const handleRenew = async (sub: CustomerSubscription) => {
        const newRenewal = addMonths(parseISO(sub.next_renewal_date), sub.renewal_period_months || 6);
        const { error } = await supabase
            .from('customer_subscriptions')
            .update({ next_renewal_date: format(newRenewal, 'yyyy-MM-dd') })
            .eq('id', sub.id);

        if (error) {
            addNotification({ type: 'error', title: 'Errore', message: error.message ?? 'Impossibile rinnovare' });
            return;
        }
        addNotification({
            type: 'success',
            title: 'Rinnovata',
            message: `Nuova scadenza: ${format(newRenewal, 'd MMMM yyyy', { locale: it })}`,
        });
        loadSubscriptions();
    };

    const getStatusBadge = (sub: CustomerSubscription) => {
        if (sub.status === 'cancelled') {
            return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Cancellata</span>;
        }
        if (sub.status === 'expired') {
            return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Da rinnovare</span>;
        }
        const st = getSubscriptionStatus(sub.next_renewal_date);
        const days = getDaysUntilRenewal(sub.next_renewal_date);
        if (st === 'expired') {
            return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Scaduta {Math.abs(days)}gg fa</span>;
        }
        if (st === 'warning') {
            return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Rinnovo tra {days}gg</span>;
        }
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Attiva</span>;
    };

    const filterTabs: { key: FilterType; label: string; count?: number }[] = [
        { key: 'all', label: 'Tutte', count: subscriptions.length },
        { key: 'active', label: 'Attive', count: stats.activeCount - stats.warning - stats.expired },
        { key: 'warning', label: 'In scadenza', count: stats.warning },
        { key: 'expired', label: 'Scadute', count: stats.expired },
        { key: 'cancelled', label: 'Cancellate' },
    ];

    return (
        <div className="space-y-4 md:space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-primary-950">
                        Subscriptions
                    </h1>
                    <p className="text-sm text-neutral-400 mt-1">Gestisci le sottoscrizioni mensili dei clienti</p>
                    <AutoInvoiceStatus run={lastRun} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" onClick={loadSubscriptions} className="h-10">
                        <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
                    </Button>
                    <Button onClick={handleNew} className="h-10 px-3 sm:px-4 flex-1 sm:flex-none shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white">
                        <Plus className="w-4 h-4 mr-2" /> Nuova Subscription
                    </Button>
                </div>
            </div>

            {/* KPI */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                {[
                    { label: 'Subscriptions Attive', value: stats.activeCount, icon: Users, color: 'text-primary-500' },
                    { label: 'MRR Totale', value: formatCurrency(stats.mrr), icon: TrendingUp, color: 'text-green-500' },
                    { label: 'In Scadenza (30gg)', value: stats.warning, icon: AlertTriangle, color: 'text-orange-500' },
                    { label: 'Scadute', value: stats.expired, icon: XCircle, color: 'text-red-500' },
                ].map((kpi, i) => (
                    <Card key={i} className="border-gray-200 bg-white">
                        <CardContent className="p-3 md:p-6">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] md:text-sm font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                                <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
                            </div>
                            <div className="text-sm md:text-2xl font-bold text-gray-900 truncate">{kpi.value}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Search + Filter tabs */}
            <div className="space-y-3">
                <Card className="p-1">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                            <Input
                                placeholder="Cerca cliente..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 h-10 md:h-12 border-none bg-transparent focus-visible:ring-0 text-base"
                            />
                        </div>
                    </div>
                </Card>

                {/* Filter tabs */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 sm:flex-wrap sm:overflow-visible">
                    {filterTabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={cn(
                                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0',
                                filter === tab.key
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            )}
                        >
                            {tab.label}
                            {tab.count !== undefined && tab.count > 0 && (
                                <span className={cn(
                                    'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                                    filter === tab.key ? 'bg-white/20' : 'bg-gray-300 text-gray-700'
                                )}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <Card className="bg-white border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400">Caricamento...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center">
                        <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500 font-medium">Nessuna subscription trovata</p>
                        <p className="text-sm text-gray-400 mt-1">Crea la prima subscription per un cliente</p>
                    </div>
                ) : (
                    <div className="table-scroll">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50">
                                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Cliente</th>
                                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Importo/mese</th>
                                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider hidden md:table-cell">Data inizio</th>
                                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Prossima fattura</th>
                                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider hidden md:table-cell">Scadenza contratto</th>
                                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Stato</th>
                                    <th className="px-4 py-3 w-12"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filtered.map(sub => (
                                    <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">
                                                {(sub.customers as any)?.company_name ?? '—'}
                                            </div>
                                            {(sub.customers as any)?.codice_cliente && (
                                                <div className="text-xs text-gray-400 font-mono">{(sub.customers as any).codice_cliente}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-gray-900">
                                            {formatCurrency(sub.monthly_amount)}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                                            {format(parseISO(sub.start_date), 'd MMM yyyy', { locale: it })}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            <div>{format(parseISO(sub.next_billing_date), 'd MMM yyyy', { locale: it })}</div>
                                            <div className="text-xs text-gray-400">
                                                {getBillingPeriodLabel(sub.billing_period_months)}
                                                {sub.auto_invoice ? ' · bozza automatica' : ' · a mano'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                                            {format(parseISO(sub.next_renewal_date), 'd MMM yyyy', { locale: it })}
                                        </td>
                                        <td className="px-4 py-3">
                                            {getStatusBadge(sub)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                            {canInvoice(sub) && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className={cn(
                                                        'h-8 px-2 text-xs',
                                                        getSubscriptionStatus(sub.next_billing_date) === 'expired'
                                                            ? 'border-red-200 text-red-700 hover:bg-red-50'
                                                            : 'border-amber-200 text-amber-700 hover:bg-amber-50'
                                                    )}
                                                    onClick={() => setInvoicingSubscription(sub)}
                                                >
                                                    <Receipt className="w-3.5 h-3.5 mr-1" /> Fattura
                                                </Button>
                                            )}
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => handleEdit(sub)}>
                                                        <Edit className="w-4 h-4 mr-2" /> Modifica
                                                    </DropdownMenuItem>
                                                    {sub.status === 'active' && (
                                                        <DropdownMenuItem onClick={() => handleRenew(sub)}>
                                                            <RotateCcw className="w-4 h-4 mr-2" /> Rinnova
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem
                                                        onClick={() => setDeletingId(sub.id)}
                                                        className="text-red-600 focus:text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" /> Elimina
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Modals */}
            <SubscriptionFormModal
                open={showForm}
                onClose={() => { setShowForm(false); setEditingSubscription(null); }}
                onSaved={loadSubscriptions}
                subscription={editingSubscription}
            />

            <SubscriptionInvoiceModal
                open={!!invoicingSubscription}
                onClose={() => setInvoicingSubscription(null)}
                onCreated={loadSubscriptions}
                subscription={invoicingSubscription}
            />

            <ConfirmDeleteDialog
                open={!!deletingId}
                onOpenChange={(v) => { if (!v) setDeletingId(null); }}
                onConfirm={handleDelete}
                title="Elimina subscription"
                description="Questa azione è irreversibile. Spariscono anche le sue bozze non ancora emesse; le fatture già emesse restano. Eliminare la subscription?"
            />
        </div>
    );
}

/**
 * Esito dell'ultimo giro notturno delle bozze. Se il giro si ferma (job spento,
 * database in errore) la data smette di avanzare: è il segnale da guardare.
 */
function AutoInvoiceStatus({ run }: { run: AutoInvoiceRun | null }) {
    if (!run) {
        return (
            <p className="text-xs text-neutral-400 mt-1">
                Bozze automatiche: nessun giro notturno eseguito finora.
            </p>
        );
    }

    const quando = format(parseISO(run.quando), "d MMM 'alle' HH:mm", { locale: it });
    const bozze = run.bozze_create === 1 ? '1 bozza preparata' : `${run.bozze_create} bozze preparate`;

    return (
        <div className="text-xs mt-1 space-y-0.5">
            <p className="text-neutral-500">
                Bozze automatiche: ultimo giro il {quando} — {bozze}.
            </p>
            {run.errori.length > 0 && (
                <p className="text-red-600">
                    {run.errori.length === 1 ? '1 contratto non elaborato' : `${run.errori.length} contratti non elaborati`}:{' '}
                    {run.errori.map((e) => `${e.cliente ?? 'cliente sconosciuto'} (${e.errore})`).join('; ')}
                </p>
            )}
        </div>
    );
}
